import { FC, useState, useMemo } from 'react';
import { AppCard, AppPage } from '../../components/general';
import { BreadcrumbItem } from '../../components/general/AppPage';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputText } from 'primereact/inputtext';
import {
  WaterfallMigrationDiff,
  WaterfallMigrationWaterfallDiff,
  WaterfallMigrationVersionDiff,
  WaterfallMigrationStepDiff,
  WaterfallMigrationImportRequest,
  WaterfallMigrationImportResult,
  WaterfallDiffAction,
} from '../../models/waterfall';

interface WaterfallMigrationReviewProps {
  diff: WaterfallMigrationDiff;
  onImport: (request: WaterfallMigrationImportRequest) => Promise<WaterfallMigrationImportResult>;
  onCancel: () => void;
  breadcrumbs?: BreadcrumbItem[];
}

const actionSeverity = (action: WaterfallDiffAction): 'success' | 'info' | 'danger' => {
  switch (action) {
    case WaterfallDiffAction.Insert: return 'success';
    case WaterfallDiffAction.Update: return 'info';
    case WaterfallDiffAction.Delete: return 'danger';
  }
};

const actionIcon = (action: WaterfallDiffAction): string => {
  switch (action) {
    case WaterfallDiffAction.Insert: return 'pi pi-plus-circle';
    case WaterfallDiffAction.Update: return 'pi pi-pencil';
    case WaterfallDiffAction.Delete: return 'pi pi-trash';
  }
};

const actionLabel = (action: WaterfallDiffAction): string => {
  switch (action) {
    case WaterfallDiffAction.Insert: return 'New';
    case WaterfallDiffAction.Update: return 'Updated';
    case WaterfallDiffAction.Delete: return 'Deleted';
  }
};

const StepDiffRow: FC<{ step: WaterfallMigrationStepDiff; depth: number }> = ({ step, depth }) => (
  <>
    <div
      className="flex align-items-center gap-2 py-2 px-3 border-bottom-1 surface-border"
      style={{ paddingLeft: `${depth * 1.5 + 0.75}rem` }}
    >
      <Tag
        severity={actionSeverity(step.action)}
        value={actionLabel(step.action)}
        icon={actionIcon(step.action)}
        style={{ fontSize: '0.7rem', minWidth: '5rem' }}
      />
      <span className="text-sm font-medium">{step.description || '(unnamed step)'}</span>
      {step.connectionNames.length > 0 && (
        <span className="text-sm text-gray-500 ml-2">
          [{step.connectionNames.join(', ')}]
        </span>
      )}
      {step.action === WaterfallDiffAction.Update && step.changedFields.length > 0 && (
        <div className="flex gap-1 ml-2 flex-wrap">
          {step.changedFields.map((f) => (
            <Tag key={f} value={f} severity="warning" style={{ fontSize: '0.65rem' }} />
          ))}
        </div>
      )}
    </div>
    {step.childStepDiffs.map((child) => (
      <StepDiffRow key={child.stepGuid} step={child} depth={depth + 1} />
    ))}
  </>
);

const VersionSection: FC<{ vd: WaterfallMigrationVersionDiff }> = ({ vd }) => {
  const stepDiffs = vd.stepDiffs ?? [];
  if (stepDiffs.length === 0 && vd.action === WaterfallDiffAction.Insert) {
    const stepCount = vd.imported?.initialSteps?.length ?? 0;
    return <span className="text-sm text-gray-500 ml-4">{stepCount} step(s)</span>;
  }
  if (stepDiffs.length === 0) return null;

  return (
    <div className="mt-2 border-1 surface-border border-round overflow-hidden">
      <div className="px-3 py-2 surface-100 text-sm font-semibold border-bottom-1 surface-border">
        Steps
      </div>
      {stepDiffs.map((sd) => (
        <StepDiffRow key={sd.stepGuid} step={sd} depth={0} />
      ))}
    </div>
  );
};

export const WaterfallMigrationReview: FC<WaterfallMigrationReviewProps> = ({
  diff,
  onImport,
  onCancel,
  breadcrumbs,
}) => {
  const defaultBreadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Waterfalls' },
    { label: 'Migration Review' },
  ];

  const waterfallDiffs = diff.waterfallDiffs;
  const allIndices = useMemo(() => waterfallDiffs.map((_, i) => i), [waterfallDiffs]);

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set(allIndices));
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<WaterfallMigrationImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importNotes, setImportNotes] = useState('');
  const [activateVersions, setActivateVersions] = useState(true);

  const totalSelected = selectedRows.size;
  const totalDiffs = waterfallDiffs.length;

  const toggleRow = (index: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAll = () => setSelectedRows(new Set(allIndices));
  const deselectAll = () => setSelectedRows(new Set());

  const handleImport = async () => {
    setShowConfirmDialog(false);
    setIsImporting(true);
    setError(null);

    const selectedWaterfallDiffs: WaterfallMigrationWaterfallDiff[] = waterfallDiffs.filter(
      (_, i) => selectedRows.has(i),
    );

    const request: WaterfallMigrationImportRequest = {
      waterfallDiffs: selectedWaterfallDiffs,
      importNotes: importNotes.trim() || null,
      activateVersions,
    };

    try {
      const result = await onImport(request);
      setImportResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const selectedInserts = waterfallDiffs.filter((wd, i) => selectedRows.has(i) && wd.action === WaterfallDiffAction.Insert).length;
  const selectedUpdates = waterfallDiffs.filter((wd, i) => selectedRows.has(i) && wd.action === WaterfallDiffAction.Update).length;

  if (isImporting) {
    return (
      <AppPage title="Migration Review" breadcrumbs={breadcrumbs || defaultBreadcrumbs}>
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-3 py-6">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span className="text-lg font-semibold">Importing changes...</span>
            <span className="text-sm text-gray-500">Please do not navigate away from this page.</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (importResult) {
    return (
      <AppPage title="Migration Review" breadcrumbs={breadcrumbs || defaultBreadcrumbs}>
        <AppCard>
          <div className="flex flex-column gap-4">
            <div className={`p-4 border-round ${importResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex align-items-center gap-2 mb-3">
                <i className={`pi ${importResult.success ? 'pi-check-circle text-green-600' : 'pi-exclamation-triangle text-red-600'} text-2xl`} />
                <span className="text-lg font-bold">
                  {importResult.success ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                </span>
              </div>
              <div className="grid">
                <div className="col-6 md:col-3">
                  <div className="text-sm text-gray-600">Waterfalls Inserted</div>
                  <div className="text-xl font-bold text-green-700">{importResult.waterfallsInserted}</div>
                </div>
                <div className="col-6 md:col-3">
                  <div className="text-sm text-gray-600">Waterfalls Updated</div>
                  <div className="text-xl font-bold text-blue-700">{importResult.waterfallsUpdated}</div>
                </div>
                <div className="col-6 md:col-3">
                  <div className="text-sm text-gray-600">Versions Inserted</div>
                  <div className="text-xl font-bold text-green-700">{importResult.versionsInserted}</div>
                </div>
                <div className="col-6 md:col-3">
                  <div className="text-sm text-gray-600">Versions Activated</div>
                  <div className="text-xl font-bold text-purple-700">{importResult.versionsActivated}</div>
                </div>
              </div>
              {importResult.warnings.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-yellow-700 mb-1">Warnings:</div>
                  {importResult.warnings.map((w, i) => (
                    <div key={i} className="text-sm text-yellow-600">&#x26A0; {w}</div>
                  ))}
                </div>
              )}
              {importResult.errors.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-red-700 mb-1">Errors:</div>
                  {importResult.errors.map((err, i) => (
                    <div key={i} className="text-sm text-red-600">&bull; {err}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button label="Back to Waterfalls" icon="pi pi-arrow-left" onClick={onCancel} />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (totalDiffs === 0 && diff.warnings.length === 0) {
    return (
      <AppPage title="Migration Review" breadcrumbs={breadcrumbs || defaultBreadcrumbs}>
        <AppCard>
          <div className="flex flex-column align-items-center gap-3 py-6">
            <i className="pi pi-check-circle text-green-500 text-5xl" />
            <span className="text-lg font-semibold">No differences found</span>
            <span className="text-sm text-gray-500">The imported file matches the current waterfall configuration.</span>
            <Button label="Back to Waterfalls" icon="pi pi-arrow-left" onClick={onCancel} className="mt-2" />
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Migration Review"
      breadcrumbs={breadcrumbs || defaultBreadcrumbs}
      headerEnd={
        <div className="flex gap-2 align-items-center">
          <span className="text-sm text-gray-600">{totalSelected} of {totalDiffs} selected</span>
          <Button label="Select All" className="p-button-sm p-button-text" onClick={selectAll} />
          <Button label="Deselect All" className="p-button-sm p-button-text" onClick={deselectAll} />
          <Button
            label="Import Selected"
            icon="pi pi-upload"
            className="p-button-sm p-button-primary"
            onClick={() => setShowConfirmDialog(true)}
            disabled={totalSelected === 0}
          />
          <Button
            label="Cancel"
            className="p-button-sm p-button-secondary"
            onClick={onCancel}
          />
        </div>
      }
    >
      {error && (
        <div className="mb-3 p-3 bg-red-50 border-round">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {diff.warnings.length > 0 && (
        <div className="mb-3 p-3 bg-yellow-50 border-round">
          <div className="flex align-items-start gap-2 mb-2">
            <i className="pi pi-exclamation-triangle text-yellow-600 text-xl mt-1" />
            <div className="font-semibold text-yellow-800">Migration Warnings</div>
          </div>
          {diff.warnings.map((w, i) => (
            <div key={i} className="text-sm text-yellow-700 ml-4">&#x26A0; {w}</div>
          ))}
        </div>
      )}

      {/* Summary bar */}
      <div className="mb-3 p-3 surface-100 border-round flex gap-4 align-items-center flex-wrap">
        <span className="font-semibold text-sm">Summary:</span>
        <Tag severity="success" value={`${selectedInserts} New`} icon="pi pi-plus-circle" />
        <Tag severity="info" value={`${selectedUpdates} Updated`} icon="pi pi-pencil" />
      </div>

      {/* Waterfall sections */}
      <div className="flex flex-column gap-3">
        {waterfallDiffs.map((wd, wi) => {
          const isSelected = selectedRows.has(wi);
          const borderColor = wd.action === WaterfallDiffAction.Insert ? 'border-green-400' : 'border-blue-400';

          return (
            <AppCard key={wi}>
              <div className={`border-left-3 ${borderColor} pl-3`}>
                <div className="flex align-items-center gap-3 mb-2">
                  <Checkbox checked={isSelected} onChange={() => toggleRow(wi)} />
                  <Tag
                    severity={actionSeverity(wd.action)}
                    value={actionLabel(wd.action)}
                    icon={actionIcon(wd.action)}
                  />
                  <span className="text-lg font-bold">{wd.waterfallName}</span>
                  <Tag value={wd.integrationType} className="ml-auto" />
                </div>

                {wd.versionDiffs.map((vd, vi) => (
                  <div key={vi} className="ml-4 mt-3">
                    <div className="flex align-items-center gap-2 mb-1">
                      <Tag
                        severity={actionSeverity(vd.action)}
                        value={actionLabel(vd.action)}
                        style={{ fontSize: '0.7rem' }}
                      />
                      <span className="text-sm font-semibold">
                        Version {vd.sourceVersionNumber > 0 ? `v${vd.sourceVersionNumber}` : '(new)'}
                      </span>
                      {vd.action === WaterfallDiffAction.Update && vd.changedFields.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {vd.changedFields.map((f) => (
                            <Tag key={f} value={f} severity="warning" style={{ fontSize: '0.65rem' }} />
                          ))}
                        </div>
                      )}
                    </div>
                    <VersionSection vd={vd} />
                  </div>
                ))}
              </div>
            </AppCard>
          );
        })}
      </div>

      {/* Confirm Dialog */}
      <Dialog
        header="Confirm Import"
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        style={{ width: '480px' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              className="p-button-secondary"
              onClick={() => setShowConfirmDialog(false)}
            />
            <Button
              label="Yes, Import"
              icon="pi pi-upload"
              className="p-button-danger"
              onClick={handleImport}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div className="bg-yellow-50 p-3 border-round">
            <div className="flex align-items-start gap-2">
              <i className="pi pi-exclamation-triangle text-yellow-600 text-xl mt-1" />
              <div>
                <p className="m-0 font-semibold text-yellow-800">This will modify waterfall configurations.</p>
                <p className="m-0 mt-1 text-sm text-yellow-700">
                  The following changes will be applied:
                </p>
              </div>
            </div>
          </div>
          <div className="surface-100 p-3 border-round">
            {selectedInserts > 0 && <div className="text-sm text-green-700">+ {selectedInserts} new waterfall(s)</div>}
            {selectedUpdates > 0 && <div className="text-sm text-blue-700">~ {selectedUpdates} updated waterfall(s)</div>}
            {selectedInserts === 0 && selectedUpdates === 0 && (
              <div className="text-sm text-gray-500">No changes</div>
            )}
          </div>
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="activateVersions"
              checked={activateVersions}
              onChange={(e) => setActivateVersions(e.checked ?? false)}
            />
            <label htmlFor="activateVersions" className="text-sm font-semibold cursor-pointer">
              Set imported versions as active
            </label>
          </div>
          <div className="flex flex-column gap-2">
            <label className="text-sm font-semibold" htmlFor="importNotes">Import Notes (optional)</label>
            <InputText
              id="importNotes"
              value={importNotes}
              onChange={(e) => setImportNotes(e.target.value)}
              placeholder="e.g., Imported from production 2026-03-28"
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </AppPage>
  );
};
